import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../../stores/authStore'

function initialsFromEmail(email: string | null | undefined): string {
  if (!email) return '?'
  const namePart = email.split('@')[0] ?? ''
  const segments = namePart.split(/[.\-_]+/).filter(Boolean)
  const letters = segments.slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '')
  return letters.join('') || namePart.slice(0, 2).toUpperCase() || '?'
}

// Avatar in the onboarding TopBar. Opens a small account menu: signed-in email
// + Sign out. Direction-agnostic (uses logical `end-0` / `text-start`) so the
// menu anchors to the inline-end under both LTR and a future RTL layout.
export default function AccountMenu() {
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const handleSignOut = async () => {
    setSigningOut(true)
    try {
      await signOut()
      navigate('/', { replace: true })
    } finally {
      setSigningOut(false)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex h-8 w-8 items-center justify-center rounded-full bg-ob-ink text-xs font-semibold text-ob-on-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ob-ink focus-visible:ring-offset-2 focus-visible:ring-offset-ob-surface"
      >
        {initialsFromEmail(user?.email)}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute end-0 z-20 mt-2 w-64 border border-ob-secondary/20 bg-ob-surface-card text-start shadow-sm"
        >
          <div className="border-b border-ob-secondary/20 px-4 py-3">
            <p className="font-label text-[11px] uppercase tracking-wider text-ob-secondary">
              Signed in as
            </p>
            <p className="mt-1 truncate font-body text-sm text-ob-on-surface">
              {user?.email ?? 'Unknown account'}
            </p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            disabled={signingOut}
            className="flex w-full items-center gap-2 px-4 py-3 text-start font-body text-sm text-ob-on-surface transition-colors hover:bg-ob-surface disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
              logout
            </span>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  )
}
